import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import './ChatAssistant.css';

const ChatAssistant = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [minimized, setMinimized] = useState(!isOpen);
  const [question, setQuestion] = useState('');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState('');
  
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load saved conversation state and minimized state from localStorage on initial mount
  useEffect(() => {
    const savedConversation = localStorage.getItem('assistantConversation');
    const savedConnected = localStorage.getItem('assistantConnected') === 'true';
    const savedMinimized = localStorage.getItem('assistantMinimized');
    
    if (savedConversation) {
      try {
        setConversation(JSON.parse(savedConversation));
      } catch (e) {
        console.error('Error parsing saved conversation:', e);
      }
    }
    
    if (savedConnected) {
      setConnected(true);
      // Reconnect to WebSocket if we were previously connected
      setTimeout(() => connectWebSocket(true), 500);
    }
    
    // Set minimized state from localStorage if available
    if (savedMinimized !== null) {
      const isMinimized = savedMinimized === 'true';
      setMinimized(isMinimized);
      
      // Sync with parent component
      if (onToggle && isMinimized !== !isOpen) {
        onToggle(!isMinimized);
      }
    }
    
    setCurrentPage(location.pathname);
  }, []);

  // Track page changes
  useEffect(() => {
    if (currentPage !== location.pathname && connected) {
      // Send a message to the assistant when the user navigates to a new page
      const newPageMessage = {
        type: 'system',
        message: `User navigated to: ${location.pathname}`
      };
      
      setConversation(prev => {
        const updatedConversation = [...prev, newPageMessage];
        localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
        return updatedConversation;
      });
      
      setCurrentPage(location.pathname);
    } else {
      setCurrentPage(location.pathname);
    }
  }, [location.pathname, connected]);

  // Save conversation to localStorage whenever it changes
  useEffect(() => {
    if (conversation.length > 0) {
      localStorage.setItem('assistantConversation', JSON.stringify(conversation));
    }
  }, [conversation]);

  // Save connection state to localStorage
  useEffect(() => {
    localStorage.setItem('assistantConnected', connected.toString());
  }, [connected]);

  // Save minimized state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assistantMinimized', minimized.toString());
  }, [minimized]);

  // Handle the externally controlled open/closed state
  useEffect(() => {
    // Only update if externally controlled state differs from current state
    if (minimized !== !isOpen) {
      setMinimized(!isOpen);
    }
  }, [isOpen]);

  // Handle minimize/maximize
  const toggleMinimize = () => {
    const newMinimizedState = !minimized;
    setMinimized(newMinimizedState);
    
    // Update localStorage
    localStorage.setItem('assistantMinimized', newMinimizedState.toString());
    
    // Notify parent component
    if (onToggle) {
      onToggle(!newMinimizedState);
    }
    
    // If maximizing and not connected, auto-connect
    if (minimized && !connected) {
      connectWebSocket();
    }
  };

  // Mark initial load as complete after first render
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current && !minimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation, status, minimized]);

  // Focus input after opening
  useEffect(() => {
    if (!minimized && inputRef.current && connected) {
      inputRef.current.focus();
    }
  }, [minimized, connected]);

  // Connect to WebSocket
  const connectWebSocket = (reconnecting = false) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    setConnecting(true);
    setError('');
    
    const wsUrl = `wss://api.andrewslearning.com/wschat/QA/andrewbrowne/`;
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log('WebSocket connection established');
      setConnected(true);
      setConnecting(false);
      
      if (!reconnecting) {
        // Only add the welcome message if not reconnecting
        const welcomeMessage = { 
          type: 'system', 
          message: `Connected to AI learning assistant. I'll stay with you as you navigate the site! I can help with subjects, guide you through tools, and track your progress.`
        };
        
        setConversation(prev => {
          const updatedConversation = [...prev, welcomeMessage];
          localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
          return updatedConversation;
        });
        
        // Send context about the current page
        const contextMessage = {
          question: `I'm a student using this website. I just navigated to ${location.pathname}. Can you tell me what I can do on this page and how you can help me learn?`
        };
        socketRef.current.send(JSON.stringify(contextMessage));
      } else {
        // If reconnecting, let the user know
        const reconnectMessage = { 
          type: 'system', 
          message: 'Reconnected to AI learning assistant. Your conversation history is preserved.'
        };
        
        setConversation(prev => {
          const updatedConversation = [...prev, reconnectMessage];
          localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
          return updatedConversation;
        });
      }
    };

    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Received:', data);

      if (data.type === 'system_message') {
        setConversation(prev => {
          const updatedConversation = [...prev, { type: 'system', message: data.message }];
          localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
          return updatedConversation;
        });
      } else if (data.type === 'status') {
        setStatus(data.message);
      } else if (data.type === 'answer') {
        setStatus('');
        setConversation(prev => {
          const updatedConversation = [
            ...prev, 
            { 
              type: 'answer', 
              question: data.question, 
              answer: data.answer 
            }
          ];
          localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
          return updatedConversation;
        });
      } else if (data.type === 'error') {
        setError(data.message);
        setStatus('');
      } else if (data.type === 'navigate') {
        // Handle navigation messages
        console.log(`Navigating to: ${data.destination}`);
        
        // Add a system message about navigation
        setConversation(prev => {
          const updatedConversation = [
            ...prev, 
            { 
              type: 'system', 
              message: `Navigating to: ${data.friendly_name || data.destination}...` 
            }
          ];
          localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
          return updatedConversation;
        });
        
        // Use React Router to navigate
        setTimeout(() => {
          navigate(data.destination);
        }, 500); // Short delay to allow the message to be displayed
      }
    };

    socketRef.current.onclose = (event) => {
      console.log('WebSocket connection closed', event);
      setConnected(false);
      setConnecting(false);
      if (!event.wasClean) {
        setError('Connection lost. Please reconnect.');
      }
    };

    socketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Failed to connect. Please try again.');
      setConnecting(false);
    };
  };

  // Send a question
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question.trim() || !connected) return;

    const message = {
      question: question
    };

    socketRef.current.send(JSON.stringify(message));
    setConversation(prev => {
      const updatedConversation = [...prev, { type: 'question', message: question }];
      localStorage.setItem('assistantConversation', JSON.stringify(updatedConversation));
      return updatedConversation;
    });
    setQuestion('');
    setError('');
  };

  // Reset conversation history
  const handleReset = () => {
    if (window.confirm('Are you sure you want to clear the conversation history?')) {
      setConversation([]);
      localStorage.removeItem('assistantConversation');
      
      // Add a system message about the reset
      setTimeout(() => {
        setConversation([{ 
          type: 'system', 
          message: 'Conversation history has been cleared. I\'m still here to help with your learning!' 
        }]);
      }, 300);
    }
  };

  // Clean up WebSocket on component unmount
  useEffect(() => {
    return () => {
      // We don't want to close the socket on unmount anymore
      // as we want it to persist between page navigations
      // Instead, we'll handle reconnection on remount
    };
  }, []);

  // Format message with enhanced styling
  const formatMessage = (text) => {
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  return (
    <div className={`chat-assistant ${minimized ? 'minimized' : ''} ${isInitialLoad ? 'initial-load' : ''}`}>
      <div className="chat-assistant-header" onClick={toggleMinimize}>
        <div className="header-content">
          <div className="assistant-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.9999 2C6.47712 2 1.99994 6.47718 1.99994 12C1.99994 17.5228 6.47712 22 11.9999 22C17.5227 22 21.9999 17.5228 21.9999 12C21.9999 6.47718 17.5227 2 11.9999 2ZM7.80386 17.6L8.69994 16.55C8.89869 16.3253 9.00045 16.0245 8.97869 15.7202C8.95694 15.4159 8.81348 15.1337 8.58994 14.94C8.06217 14.4468 7.75338 13.7392 7.73994 13C7.73994 11.54 8.73994 10.5 9.99994 10.5H13.9999C15.2599 10.5 16.2599 11.54 16.2599 13C16.2599 13.97 15.7599 14.82 14.9999 15.25V17C14.9999 17.5523 14.5522 18 13.9999 18H9.99994C9.60617 17.9987 9.22636 17.8593 8.92635 17.6062C8.62633 17.353 8.42625 17.0014 8.36414 16.615C8.30203 16.2286 8.38116 15.8316 8.58625 15.4977C8.79133 15.1637 9.1094 14.913 9.47994 14.79C9.69994 14.72 9.89994 14.57 9.99994 14.36C10.1499 14.04 9.99994 13.7 9.73994 13.66C9.43619 13.5908 9.17071 13.4176 8.99242 13.1721C8.81413 12.9266 8.73476 12.6248 8.76994 12.33C8.76994 12.18 8.83994 12.04 8.93994 11.94C9.07618 11.8031 9.25635 11.7253 9.44652 11.7222C9.6367 11.7191 9.81961 11.7909 9.96051 11.9229C10.1014 12.0549 10.1854 12.2371 10.1951 12.4299C10.2048 12.6227 10.1395 12.8121 10.0099 12.96C9.96691 13.0031 9.93949 13.0574 9.93206 13.1153C9.92464 13.1732 9.93758 13.2317 9.96886 13.2811C9.99994 13.33 10.0399 13.37 10.0899 13.4C10.1399 13.43 10.1999 13.44 10.2599 13.44C10.8099 13.44 11.2599 13.9 11.2599 14.44C11.2617 14.7038 11.1726 14.9581 11.0077 15.1548C10.8429 15.3516 10.6127 15.4782 10.3599 15.51C10.1199 15.56 9.86994 15.61 9.61994 15.66C9.41994 15.7 9.18994 15.74 8.97994 15.78C8.81539 15.8133 8.66993 15.9033 8.56994 16.03L7.79994 17C7.72162 17.0918 7.66871 17.2017 7.64587 17.3202C7.62302 17.4386 7.63102 17.5615 7.66903 17.6759C7.70704 17.7903 7.77372 17.892 7.86234 17.9699C7.95096 18.0478 8.05837 18.0989 8.17295 18.1177C8.28752 18.1364 8.40511 18.1221 8.51292 18.0762C8.62074 18.0303 8.71493 17.9546 8.78594 17.8581C8.85694 17.7615 8.90146 17.6475 8.91483 17.5275C8.9282 17.4075 8.90989 17.2863 8.85994 17.18L9.13994 16.85C9.28994 16.67 9.54994 16.64 9.73994 16.76L9.99994 16.96C10.0754 17.0176 10.1647 17.0551 10.2587 17.0689C10.3526 17.0827 10.4483 17.0723 10.5363 17.0388C10.6242 17.0053 10.7013 16.9499 10.7594 16.8785C10.8175 16.807 10.8546 16.7224 10.8666 16.6324C10.8786 16.5424 10.8651 16.4508 10.8275 16.3676C10.7898 16.2845 10.7294 16.2134 10.6532 16.1625C10.577 16.1116 10.4879 16.0829 10.3959 16.0798C10.3039 16.0767 10.2129 16.0993 10.1329 16.1448C10.0529 16.1903 9.98673 16.2568 9.93994 16.34C9.87994 16.49 9.73994 16.58 9.55994 16.58C9.47994 16.58 9.41994 16.56 9.34994 16.53C9.15994 16.41 9.07994 16.2 9.13994 16L9.44994 15.75C9.54994 15.68 9.65994 15.68 9.75994 15.65C10.0599 15.6 10.3499 15.54 10.6499 15.48C10.3374 15.4191 10.0519 15.2576 9.83994 15.02C9.68994 14.85 9.59994 14.65 9.55994 14.43C9.20994 14.53 8.93994 14.8 8.79994 15.14C8.67994 15.38 8.62994 15.65 8.64994 15.93C8.69994 16.26 8.83994 16.56 9.04994 16.77L8.36994 17.58C8.17994 17.58 7.99994 17.51 7.85994 17.39C7.71994 17.27 7.63994 17.09 7.63994 16.9V16.89L7.79994 16.69L7.80386 17.6ZM14.9999 16.5V15.32C14.9999 15.0879 15.0842 14.8656 15.234 14.699C15.3837 14.5325 15.5922 14.4371 15.8124 14.4306C15.8374 14.4301 15.8623 14.4301 15.8873 14.4306C16.3899 14.4566 16.8699 14.28 17.2199 13.93C17.5599 13.59 17.7599 13.11 17.7599 12.6V12C17.7599 11.45 17.3099 11 16.7599 11H14.2599V10.5H16.7599C17.5908 10.5 18.2677 11.1686 18.2677 12V12.6C18.2677 13.32 17.9768 14.01 17.4599 14.53C17.1968 14.7963 16.889 15.0119 16.5499 15.17V16.5H14.9999Z" />
            </svg>
          </div>
          <h3>Learning Assistant</h3>
          {connected && <div className="status-dot connected"></div>}
          <div className="header-actions">
            {connected && !minimized && (
              <button 
                className="reset-btn" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleReset();
                }}
                title="Clear conversation history"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4V2C12 1.44772 11.5523 1 11 1H9C8.44772 1 8 1.44772 8 2V4H4C3.44772 4 3 4.44772 3 5V7C3 7.55228 3.44772 8 4 8H4.5L5.15661 19.8874C5.22684 21.0943 6.22259 22 7.43151 22H16.5685C17.7774 22 18.7732 21.0943 18.8434 19.8874L19.5 8H20C20.5523 8 21 7.55228 21 7V5C21 4.44772 20.5523 4 20 4H16V2C16 1.44772 15.5523 1 15 1H13C12.4477 1 12 1.44772 12 2V4ZM10 4V3H14V4H10ZM5 6H19V6.98828L18.1145 20.0883C18.0957 20.5034 17.7535 20.8314 17.3376 20.8314H6.66239C6.24647 20.8314 5.90425 20.5034 5.88549 20.0883L5 7V6ZM8 8C8.55229 8 9 8.44771 9 9V18C9 18.5523 8.55229 19 8 19C7.44772 19 7 18.5523 7 18V9C7 8.44771 7.44772 8 8 8ZM12 8C12.5523 8 13 8.44771 13 9V18C13 18.5523 12.5523 19 12 19C11.4477 19 11 18.5523 11 18V9C11 8.44771 11.4477 8 12 8ZM16 8C16.5523 8 17 8.44771 17 9V18C17 18.5523 16.5523 19 16 19C15.4477 19 15 18.5523 15 18V9C15 8.44771 15.4477 8 16 8Z" />
                </svg>
              </button>
            )}
            <button 
              className="minimize-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleMinimize();
              }}
            >
              {minimized ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 12H6M12 6V18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 12H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        {currentPage && !minimized && connected && (
          <div className="current-page-info">
            <span>Current page: {currentPage}</span>
          </div>
        )}
      </div>

      {!minimized && (
        <>
          <div className="chat-assistant-body">
            <div className="conversation-container">
              {conversation.length === 0 && !status && (
                <div className="empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>Connect to start your personalized learning journey! I'll stay with you as you browse.</p>
                </div>
              )}
              
              {conversation.map((item, index) => (
                <div key={index} className={`message ${item.type}`}>
                  {item.type === 'system' && (
                    <div className="system-message">{formatMessage(item.message)}</div>
                  )}
                  
                  {item.type === 'question' && (
                    <div className="user-question">
                      <strong>You</strong> {formatMessage(item.message)}
                    </div>
                  )}
                  
                  {item.type === 'answer' && (
                    <div className="ai-answer">
                      <div className="answer">{formatMessage(item.answer)}</div>
                    </div>
                  )}
                </div>
              ))}
              
              {status && (
                <div className="status-message">
                  <Spinner animation="border" size="sm" /> {status}
                </div>
              )}
              
              {error && (
                <div className="error-message">
                  Error: {error}
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="chat-assistant-footer">
            {!connected ? (
              <Button 
                onClick={connectWebSocket}
                disabled={connecting}
                className={`connect-btn ${connecting ? "btn-loading" : ""}`}
              >
                {connecting ? (
                  <>
                    <Spinner animation="border" size="sm" /> Connecting...
                  </>
                ) : (
                  'Start Learning Assistant'
                )}
              </Button>
            ) : (
              <Form onSubmit={handleSubmit}>
                <div className="input-container">
                  <Form.Control
                    ref={inputRef}
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask about the current page or any subject..."
                    disabled={!connected}
                  />
                  <Button 
                    type="submit" 
                    disabled={!connected || !question.trim()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatAssistant; 